<script>
import { locationInfo, storeInfos, mapLoaded } from '../store/inapp-store'
import { onMount, onDestroy, createEventDispatcher } from 'svelte'
import API from '../store/config';

const dispatch = createEventDispatcher()

export let zoomLevel = 15
export let showMapNavbar = false

let mapContainer
let map
let datasource
let azureMapsLoaded = false
let azureMapsKey = null

onMount(async () => {
  await loadAzureMapsSDK()
  await fetchAzureMapsKey()
  // 等待下一個 tick 確保 DOM 元素已綁定
  setTimeout(initializeMap, 100)
})

onDestroy(() => {
  if (map) {
    map.dispose()
  }
})

async function fetchAzureMapsKey() {
  try {
    const response = await fetch(`${API}/azureMapKey`)
    if (response.ok) {
      const data = await response.json()
      azureMapsKey = data.key
    } else {
      console.error('Failed to fetch Azure Maps key:', response.statusText)
    }
  } catch (error) {
    console.error('Error fetching Azure Maps key:', error)
  }
}

async function loadAzureMapsSDK() {
  return new Promise((resolve, reject) => {
    if (window.atlas) {
      azureMapsLoaded = true
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/2/atlas.min.js'
    script.onload = () => {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/2/atlas.min.css'
      document.head.appendChild(link)
      
      azureMapsLoaded = true
      resolve()
    }
    script.onerror = reject
    document.head.appendChild(script)
  })
}

function initializeMap() {
  console.log('Attempting to initialize map...', {
    azureMapsLoaded,
    mapContainer: !!mapContainer,
    azureMapsKey: !!azureMapsKey,
    windowAtlas: !!window.atlas
  })
  
  if (!azureMapsLoaded || !mapContainer || !azureMapsKey) {
    console.log('Map initialization conditions not met:', { 
      azureMapsLoaded, 
      mapContainer: !!mapContainer, 
      azureMapsKey: !!azureMapsKey 
    })
    // 重試初始化
    if (azureMapsKey && azureMapsLoaded) {
      setTimeout(initializeMap, 200)
    }
    return
  }
  
  try {
    console.log('Creating Azure Map instance...')
    // 使用台北市政府作為預設中心點
    const defaultCenter = [121.5654, 25.0330] // 台北市政府
    const center = ($locationInfo.longitude && $locationInfo.latitude) 
      ? [parseFloat($locationInfo.longitude), parseFloat($locationInfo.latitude)]
      : defaultCenter
      
    console.log('Map center:', center)
    
    map = new window.atlas.Map(mapContainer, {
      center: center, 
      zoom: zoomLevel,
      authOptions: {
        authType: 'subscriptionKey',
        subscriptionKey: azureMapsKey
      },
      style: 'road',
      language: 'zh-TW',
      showLogo: false,
      showFeedbackLink: false,
      // 優化性能設定
      enableAccessibility: false,
      renderWorldCopies: false,
      scrollZoomOptions: {
        duration: 300
      }
    })
    console.log('Azure Map instance created successfully')
  } catch (error) {
    console.error('Azure Maps initialization error:', error)
    return
  }

  map.events.add('ready', () => {
    console.log('Map ready, setting up data source and markers...')
    
    // 載入自訂圖標
    map.imageSprite.add('cup-icon', '/img/icon-ieco-cup.png').then(() => {
      console.log('Custom cup icon loaded successfully')
      if ($storeInfos && $storeInfos.length > 0) {
        addStoreMarkers()
      }
    }).catch((error) => {
      console.error('Failed to load custom cup icon:', error)
      if ($storeInfos && $storeInfos.length > 0) {
        addStoreMarkers()
      }
    })
    
    // 門市標記數據源
    datasource = new window.atlas.source.DataSource()
    map.sources.add(datasource)

    // 門市標記層
    const symbolLayer = new window.atlas.layer.SymbolLayer(datasource, null, {
      iconOptions: {
        image: 'cup-icon', // 使用自訂杯子圖標
        allowOverlap: true,
        anchor: 'center',
        size: 0.6 // 調整為較小尺寸
      },
      textOptions: {
        textField: ['get', 'title'],
        color: '#000000',
        offset: [0, 3], // 調整文字位置
        size: 12
      }
    })

    map.layers.add(symbolLayer)

    // 點擊標記時發送門市選擇事件
    map.events.add('click', symbolLayer, (e) => {
      if (e.shapes && e.shapes.length > 0) {
        const properties = e.shapes[0].getProperties()
        const position = e.shapes[0].getCoordinates()
        console.log('Marker clicked, dispatching storeSelected event:', properties)
        
        // 將選中的門市移動到畫面中央
        map.setCamera({
          center: position,
          duration: 600,
          type: 'ease'
        })
        
        // 從原始數據中找到完整的門市資訊
        const fullStoreData = $storeInfos.find(store => store.storeId === properties.storeId)
        
        if (fullStoreData) {
          dispatch('storeSelected', fullStoreData)
        } else {
          console.warn('Store data not found for storeId:', properties.storeId)
        }
      }
    })

    $mapLoaded = true
  })

  // 使用節流優化縮放事件，減少卡頓
  let zoomTimeout
  map.events.add('zoom', () => {
    if (zoomTimeout) clearTimeout(zoomTimeout)
    zoomTimeout = setTimeout(() => {
      zoomLevel = map.getCamera().zoom
    }, 100)
  })
}

function addStoreMarkers() {
  console.log('addStoreMarkers called, datasource:', !!datasource, 'storeInfos:', $storeInfos?.length || 0)
  
  if (!datasource || !$storeInfos || !Array.isArray($storeInfos)) {
    console.log('Cannot add store markers - missing requirements')
    return
  }

  console.log('Adding', $storeInfos.length, 'store markers to map')

  const features = $storeInfos.map(store => {
    // console.log('Processing store:', store.storeName, 'at', store.lon, store.lat)
    return new window.atlas.data.Feature(
      new window.atlas.data.Point([parseFloat(store.lon), parseFloat(store.lat)]), {
        title: store.storeName,
        storeId: store.storeId,
        address: store.address
      }
    )
  })

  datasource.add(features)
  console.log('Store markers added to datasource')
}


$: {
  if (map && $locationInfo.latitude && $locationInfo.longitude) {
    const center = [parseFloat($locationInfo.longitude), parseFloat($locationInfo.latitude)]
    map.setCamera({
      center: center,
      zoom: zoomLevel
    })
  }
}

$: {
  if (map && zoomLevel) {
    map.setCamera({ zoom: zoomLevel })
  }
}

$: {
  if ($storeInfos && datasource) {
    datasource.clear()
    addStoreMarkers()
  }
}

</script>

<style>
  .azure-map-view {
    position: fixed;
    border: 0;
    margin-top: -0.45rem;
    min-height: calc(100vh + 2.725rem);
    max-width: 420px;
    width: 100%;
    touch-action: manipulation;
    z-index: 0;
  }
  .azure-map-view-full {
    position: fixed;
    border: 0;
    margin-top: 0rem;
    min-height: calc(100vh + 2.725rem);
    max-width: 420px;
    width: 100%;
    touch-action: manipulation;
    z-index: 0;
  }    
</style>

<div 
  bind:this={mapContainer} 
  class="{showMapNavbar ? 'azure-map-view-full': 'azure-map-view'}"
  id="azure-map"
></div>